import React from 'react';
import { Box, Heading, Text, VStack, HStack, Icon, Progress, Button, Badge } from '@chakra-ui/react';
import { FaCheckCircle, FaRegCircle, FaArrowRight } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { ONBOARDING_STEPS, computeOnboardingProgress, TIME_TO_FIRST_SIGNAL_TARGET_HOURS } from '../config/onboarding';

/**
 * Onboarding checklist to the first live behavioral signal (<48h).
 * Reads the canonical steps from config/onboarding.js and reflects real
 * workspace state via the `ctx` prop. Fully keyboard-navigable and
 * screen-reader labelled.
 */
const OnboardingChecklist = ({ ctx = {} }) => {
    const navigate = useNavigate();
    const progress = computeOnboardingProgress(ctx);

    if (progress.complete) return null;

    return (
        <Box
            as="section"
            aria-label="Onboarding checklist"
            w="full"
            bg="white"
            borderWidth="1px"
            borderColor="brand.divider"
            borderRadius="xl"
            boxShadow="sm"
            p={{ base: 5, md: 6 }}
        >
            <HStack justify="space-between" align="flex-start" mb={2} flexWrap="wrap" gap={2}>
                <Box>
                    <Heading size="sm" color="brand.navy">Get to your first live signal</Heading>
                    <Text fontSize="sm" color="gray.600" mt={1}>
                        Standard setup reaches a first behavioral signal in under {TIME_TO_FIRST_SIGNAL_TARGET_HOURS} hours — no engineering needed.
                    </Text>
                </Box>
                <Badge colorScheme="teal" fontSize="sm" px={3} py={1} borderRadius="full">
                    {progress.done}/{progress.total} done
                </Badge>
            </HStack>

            <Progress
                value={progress.pct}
                size="sm"
                colorScheme="teal"
                borderRadius="full"
                mb={4}
                aria-label={`Onboarding ${progress.pct} percent complete`}
            />

            <VStack align="stretch" spacing={3}>
                {ONBOARDING_STEPS.map((step) => {
                    let done = false;
                    try { done = step.isComplete(ctx); } catch (e) { done = false; }
                    const isNext = progress.nextStep && progress.nextStep.key === step.key;
                    return (
                        <HStack
                            key={step.key}
                            align="flex-start"
                            spacing={3}
                            p={3}
                            borderRadius="md"
                            bg={isNext ? 'brand.tealSoft' : 'transparent'}
                            borderWidth={isNext ? '1px' : '0'}
                            borderColor="brand.teal"
                        >
                            <Icon
                                as={done ? FaCheckCircle : FaRegCircle}
                                color={done ? 'green.500' : 'gray.400'}
                                boxSize={5}
                                mt={0.5}
                                aria-hidden="true"
                            />
                            <Box flex={1}>
                                <Text fontWeight="semibold" color="gray.800" textDecoration={done ? 'line-through' : 'none'}>
                                    {step.title}
                                </Text>
                                <Text fontSize="sm" color="gray.600">{step.description}</Text>
                            </Box>
                            {isNext && step.cta ? (
                                <Button
                                    size="sm"
                                    colorScheme="teal"
                                    rightIcon={<FaArrowRight />}
                                    onClick={() => navigate(step.href)}
                                >
                                    {step.cta}
                                </Button>
                            ) : null}
                        </HStack>
                    );
                })}
            </VStack>
        </Box>
    );
};

export default OnboardingChecklist;
